import { ContentSwitcher, Pill, Skeleton } from '@iwoca/orion';
import { FormProvider, useForm } from 'react-hook-form';

import { displayCurrency } from '../../../../../utils/DisplayCurrency';
import { TCustomerSpendingLimit } from '../../../Customers.types';

const SPENDING_LIMIT_DURATION_ORDER = ['3', '12', '1'];

const orderSpendingLimits = (limits: TCustomerSpendingLimit[]) => {
  return limits.sort((a, b) => {
    return (
      SPENDING_LIMIT_DURATION_ORDER.indexOf(a.duration_in_months.toString()) -
      SPENDING_LIMIT_DURATION_ORDER.indexOf(b.duration_in_months.toString())
    );
  });
};

const fallbackLimit: TCustomerSpendingLimit = {
  available_amount: 0,
  duration_in_months: 3,
  is_valid: true,
  timeout: new Date().toISOString(),
};

export const SpendingLimitsContentSwitcher = ({
  limits,
}: {
  limits: TCustomerSpendingLimit[];
}) => {
  const orderedLimits = orderSpendingLimits(limits);

  const methods = useForm({
    defaultValues: { limit: orderedLimits[0] || fallbackLimit },
  });

  const limit = methods.watch('limit');

  const daysUntilExpiry = Math.ceil(
    (new Date(limit.timeout).getTime() - Date.now()) / (1000 * 60 * 60 * 24) +
      1,
  );

  return (
    <div>
      <div className="mb-l mt-s text-l font-bold">Spending limit</div>
      <div className="w-full rounded-l border border-secondary-90 px-xl py-s">
        <FormProvider {...methods}>
          <div className="flex flex-col gap-s">
            <ContentSwitcher
              value={limit.duration_in_months.toString()}
              className="my-l !w-full justify-between"
            >
              {orderedLimits.map((limit) => (
                <ContentSwitcher.Button
                  key={limit.duration_in_months}
                  id={limit.duration_in_months.toString()}
                  className="w-full cursor-pointer justify-center"
                  onClick={() => methods.setValue('limit', limit)}
                >
                  {limit.duration_in_months === 1
                    ? '30 days'
                    : `${limit.duration_in_months} months`}
                </ContentSwitcher.Button>
              ))}
            </ContentSwitcher>
            <div className="mb-s flex flex-col gap-s">
              <div className="flex items-end text-xl font-reg xs:flex-col xs:items-start xs:gap-s">
                <div>
                  <b className="text-2xl font-bold">
                    {displayCurrency(limit.available_amount, 0)}
                  </b>{' '}
                  Left to spend
                </div>
                {limit.is_valid ? (
                  <Pill
                    className="mb-xs ml-s h-max w-max xs:m-0"
                    variant="green"
                  >
                    Active
                  </Pill>
                ) : (
                  <Pill
                    className="mb-xs ml-l h-max w-max xs:m-0"
                    variant="london"
                  >
                    Subject to approval
                  </Pill>
                )}
              </div>
              <div className="grid grid-cols-[1fr_140px] items-end text-s font-reg xs:grid-cols-1">
                <div>
                  {!limit.is_valid &&
                    'This was the buyers last active limit. Their limit needs to be reassessed every 30 days.'}
                </div>
                <div className="text-end text-structure-40 xs:mt-l xs:text-start">
                  {daysUntilExpiry > 0
                    ? `${daysUntilExpiry} day${daysUntilExpiry > 1 ? 's' : ''} left till expiry`
                    : `Expired ${-daysUntilExpiry + 1} day${-daysUntilExpiry + 1 > 1 ? 's' : ''} ago`}
                </div>
              </div>
            </div>
          </div>
        </FormProvider>
      </div>
    </div>
  );
};

export const SpendingLimitsLoading = () => {
  return (
    <div>
      <div className="mb-l mt-s text-l font-bold">Spending limit</div>
      <div className="w-full rounded-l border border-secondary-90 px-xl py-s">
        <ContentSwitcher className="my-l !h-3xl !w-full">
          <></>
        </ContentSwitcher>
        <Skeleton count={3} lastLineLength="medium" />
      </div>
    </div>
  );
};
